// src/components/pat/PatCoarseMirrorPanel.jsx
// ---------------------------------------------------------
// Coarse semantic mirror with analyst affordances
// Purpose:
//   • Externalise linguistic structure
//   • Reduce cognitive load before CATWOE / OODA
//   • Support promotion, suppression, reframing
//   • UI-only, reversible, non-inferential
// ---------------------------------------------------------

import React, { useState } from "react";
import { Eye, EyeOff, ArrowUpCircle, Edit3 } from "lucide-react";

/*
Expected prop shape:

coarseTensor = {
  clauses: [
    {
      text: string,
      features: { ... }
    }
  ],
  summary_flags: { ... }
}
*/

const FEATURE_LABELS = {
  has_candidate_causes: "multiple entities",
  has_candidate_outcome: "outcome language",
  has_directional_language: "directional terms",
  has_associational_language: "associational terms",
  has_intervention_language: "intervention framing",
  has_normative_language: "normative language",
  has_decision_language: "decision framing",
  has_uncertainty_language: "uncertainty",
  has_epistemic_language: "epistemic language",
};

export default function PatCoarseMirrorPanel({ coarseTensor, onProceed }) {
  if (
    !coarseTensor ||
    !Array.isArray(coarseTensor.clauses) ||
    coarseTensor.clauses.length === 0
  ) {
    return null;
  }

  const { clauses } = coarseTensor;

  // --------------------------------------------------
  // Analyst interaction state (UI-only)
  // --------------------------------------------------
  const [promoted, setPromoted] = useState(new Set());
  const [muted, setMuted] = useState(new Set());
  const [reframed, setReframed] = useState({});

  const toggleSet = (setter, set, key) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 p-4 space-y-4">
      {/* Header */}
      <h3 className="text-sm font-semibold text-sky-300">
        Structural mirror of your input
      </h3>

      <p className="text-xs text-slate-400 italic">
        This panel reflects the linguistic structure of your input. You may
        highlight, mute, or reframe fragments to reduce semantic noise. These
        actions do not assert causality and remain fully reversible.
      </p>

      {/* Clause list */}
      <div className="space-y-3">
        {clauses.map((clause, idx) => {
          const id = `c${idx}`;
          const isPromoted = promoted.has(id);
          const isMuted = muted.has(id);

          const activeFeatures = Object.entries(clause.features || {})
            .filter(([, v]) => v)
            .map(([k]) => FEATURE_LABELS[k])
            .filter(Boolean);

          return (
            <div
              key={id}
              className={[
                "border rounded-md p-3 transition-all",
                isMuted
                  ? "border-slate-700 bg-slate-800/30 opacity-40"
                  : "border-slate-700 bg-slate-800/60",
                isPromoted && "ring-1 ring-sky-400",
              ].join(" ")}
            >
              {/* Clause text */}
              <div className="text-xs text-slate-200 mb-2">{clause.text}</div>

              {/* Feature tags */}
              {activeFeatures.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {activeFeatures.map((label) => (
                    <span
                      key={label}
                      className="px-2 py-0.5 text-[11px] rounded bg-slate-700 text-slate-200"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {/* Analyst controls */}
              <div className="flex items-center gap-3 text-slate-400">
                {/* Promote */}
                <button
                  title="Promote: highlight as causally relevant"
                  onClick={() => toggleSet(setPromoted, promoted, id)}
                  className="hover:text-sky-300"
                >
                  <ArrowUpCircle size={14} />
                </button>

                {/* Mute */}
                <button
                  title="De-emphasise: treat as contextual background"
                  onClick={() => toggleSet(setMuted, muted, id)}
                  className="hover:text-amber-300"
                >
                  {isMuted ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>

                {/* Reframe */}
                <button
                  title="Reframe: rewrite in neutral causal-candidate language"
                  onClick={() =>
                    setReframed((prev) => ({
                      ...prev,
                      [id]: prev[id] ?? "",
                    }))
                  }
                  className="hover:text-emerald-300"
                >
                  <Edit3 size={14} />
                </button>
              </div>

              {/* Reframing input */}
              {id in reframed && (
                <div className="mt-2">
                  <textarea
                    rows={2}
                    value={reframed[id]}
                    onChange={(e) =>
                      setReframed((prev) => ({
                        ...prev,
                        [id]: e.target.value,
                      }))
                    }
                    placeholder="Analyst reframe (optional, non-binding)"
                    className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-xs text-slate-200 italic"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Proceed control */}
      <div className="flex justify-end pt-3 border-t border-slate-700">
        <button
          type="button"
          onClick={() =>
            onProceed?.({
              reframedClauses: reframed,
            })
          }
          className="px-4 py-2 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-500"
        >
          Proceed with this interpretation
        </button>
      </div>
    </div>
  );
}
